<template>
  <div>
    <div class="flex items-center mb-5">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
        Order History
      </h3>
      <hr class="flex-1 border-primary mx-4" />
    </div>

    <div>
      <DataTable
          :value="orderRowsList"
          dataKey="rowKey"
          :paginator="true"
          :rows="10"
          :lazy="false"
          :loading="loading"
      >
        <Column field="orderNumber" header="Order Number" :headerStyle="{ width: '15%' }">
          <template #body="slotProps">
            <button
                type="button"
                class="text-blue-600 hover:underline font-semibold"
                @click="goToPurchaseOrder(slotProps.data)"
            >
              {{ slotProps.data.orderNumber }}
            </button>
          </template>
        </Column>

        <Column field="orderDate" header="Date" :headerStyle="{ width: '13%' }"></Column>
        <Column field="supplierName" header="Supplier" :headerStyle="{ width: '22%' }"></Column>
        <Column field="quantity" header="Qty" :headerStyle="{ width: '8%' }" headerClass="column-h-text-center" :styles="{ 'text-align': 'center' }"></Column>
        <Column field="unitPrice" header="Price Per Unit GBP" :headerStyle="{ width: '14%' }" headerClass="column-h-text-center" :styles="{ 'text-align': 'right' }"></Column>
        <Column field="totalPrice" header="Total Price GBP" :headerStyle="{ width: '14%' }" headerClass="column-h-text-center" :styles="{ 'text-align': 'right' }"></Column>
        <Column field="statusLabel" header="Status" :headerStyle="{ width: '14%' }">
          <template #body="slotProps">
            <span class="px-2 rounded-lg" :class="statusClass(slotProps.data.status)">
              {{ slotProps.data.statusLabel }}
            </span>
          </template>
        </Column>

        <template #empty>
          <div class="w-full text-center text-gray-500 py-4">No purchase orders found for this product code.</div>
        </template>
      </DataTable>
    </div>
  </div>
</template>

<script>
import PurchaseOrderService from "@/services/purchaseService";

export default {
  name: "OrderHistoryList",
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      orders: [],
    };
  },
  computed: {
    productCode() {
      return (this.product && this.product.product_code) || "";
    },
    orderRowsList() {
      const wanted = this.productCode.toUpperCase();
      // One row per matching order line (an order can contain the product on
      // several lines).
      return this.orders.flatMap((order) => {
        const lines = (order.items || []).filter(
          (i) => (i.product_code || "").toUpperCase() === wanted,
        );
        return lines.map((line, index) => ({
          rowKey: `${order.purchase_order_id}-${index}`,
          purchaseOrderId: order.purchase_order_id,
          orderNumber: order.purchase_order_no || "N/A",
          orderDate: order.order_date
            ? this.$moment.unix(order.order_date).format("DD-MM-YYYY")
            : "N/A",
          supplierName:
            order.supplier && order.supplier.name ? order.supplier.name : "N/A",
          quantity: typeof line.quantity === "number" ? line.quantity : "N/A",
          unitPrice:
            typeof line.unit_price === "number"
              ? `£${parseFloat(line.unit_price).toFixed(2)}`
              : "N/A",
          totalPrice:
            typeof line.total === "number"
              ? `£${parseFloat(line.total).toFixed(2)}`
              : "N/A",
          status: order.status || "",
          statusLabel: this.formatStatus(order.status),
        }));
      });
    },
  },
  watch: {
    productCode: {
      immediate: true,
      handler(code) {
        if (code) this.loadOrderHistory();
      },
    },
  },
  methods: {
    async loadOrderHistory() {
      this.loading = true;
      try {
        const params =
          `?productCode=${encodeURIComponent(this.productCode)}` +
          `&limit=200&page=1&orderBy=order_date&order=-1`;
        const result = await PurchaseOrderService.searchAllPurchaseOrders(params);
        this.orders = (result && result.data) || [];
      } catch (error) {
        console.error("Error loading order history:", error);
        this.orders = [];
      } finally {
        this.loading = false;
      }
    },
    // Same display rule as the Purchases index: uppercase, and 'items added'
    // is shown as PENDING.
    formatStatus(status) {
      if (status && status.toLowerCase() === "items added") {
        return "PENDING";
      }
      return status ? status.toUpperCase() : "N/A";
    },
    statusClass(status) {
      switch ((status || "").toLowerCase()) {
        case "approved":
          return "bg-green-200 text-green-700";
        case "received":
          return "bg-blue-200 text-blue-700";
        case "part received":
          return "bg-orange-200 text-orange-700";
        case "pending":
        case "items added":
          return "bg-yellow-200 text-yellow-700";
        case "cancelled":
          return "bg-red-200 text-red-700";
        default:
          return "bg-gray-200 text-gray-700";
      }
    },
    goToPurchaseOrder(row) {
      if (!row || !row.purchaseOrderId) return;
      this.$router.push({
        name: "purchase-edit",
        params: { purchase_order_id: row.purchaseOrderId },
      });
    },
  },
};
</script>

<style scoped>
::v-deep .column-h-text-center .p-column-header-content {
  text-align: center;
  display: block !important;
}
</style>
